<script setup lang="ts">
import { SancareDateRangePicker, SancareMultiselect } from '@sancare/typescript-commons'
import dayjs from 'dayjs'
import { computed } from 'vue'
import { useStore } from 'vuex'

import { statusLabels } from '@/misc/constants'
import { AutomationModeEnum } from '@/store/modules/automation/types'

export interface AutomationStayListCriteriaProps {
  automationMode: AutomationModeEnum
}

const store = useStore()
const props = defineProps<AutomationStayListCriteriaProps>()
const emit = defineEmits<{
  (e: 'set-stay-list-param'): void
  (e: 'refresh-staylist',
    { resetStaySelection, refreshUrl }: { resetStaySelection: boolean, refreshUrl: boolean }
  ): void
}>()

const earlyLimit = computed<dayjs>(() => store.state.settings.earlyLimit)
const lateLimit = computed<dayjs>(() => store.state.settings.lateLimit)
const pageSize = computed<number>(() => store.state.automation.automationStayListParams.pageSize)
const showSeen = computed<boolean>(() => store.state.automation.automationStayListParams.showSeen)
const showStatus = computed<string[]>(() => store.state.automation.automationStayListParams.showStatus)
const multicheckStatus = computed(() => {
  const multiCheckStatus = []
  for (const [key, text] of Object.entries(statusLabels)) {
    multiCheckStatus.push({ value: key, label: text })
  }
  return multiCheckStatus
})

function setParameter(type: string, value: string | number) {
  emit('set-stay-list-param', type, value)
}

function setDateRange(earlyLimit: dayjs, lateLimit: dayjs) {
  store.commit('setEarlyLimit', earlyLimit)
  store.commit('setLateLimit', lateLimit)
  emit('refresh-staylist', { resetStaySelection: true, refreshUrl: true })
}

function multicheckStatusUpdate(newMultiCheckStatus) {
  if (newMultiCheckStatus === null) {
    newMultiCheckStatus = Object.keys(statusLabels)
  }
  if (JSON.stringify(showStatus.value) === JSON.stringify(newMultiCheckStatus)) {
    return
  }
  setParameter('showStatus', newMultiCheckStatus)
}
</script>

<template>
  <div
    name="stayListCriteria"
    class="stay-list-criteria card mb-3"
  >
    <div class="card-body">
      <div class="row px-3 py-3 align-items-end">
        <h2>Paramètres</h2>
      </div>
      <div class="row align-items-center">
        <div class="col">
          <div>
            <div
              class="row px-2 align-items-center"
              style="flex-wrap: nowrap;"
            >
              <div class="col-auto px-2">
                <sancare-date-range-picker
                  :start-date="earlyLimit"
                  :end-date="lateLimit"
                  @update:range="setDateRange"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-2">
          <sancare-multiselect
            :title="'Statuts'"
            :selected-items="showStatus"
            :items="multicheckStatus"
            :all-label="'Tous'"
            :none-label="'Aucun'"
            :display-list-limit="2"
            @update-selection="multicheckStatusUpdate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
